import { useEffect, useState, useContext, createContext, useCallback, useMemo, ReactNode } from 'react'
import { CognitoUser, CognitoIdToken } from 'amazon-cognito-identity-js'
import { Hub } from '@aws-amplify/core'
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { hubCallback } from '../config/amplify/hub'
import { setRequestInterceptor } from '../config/axios'
import axios from 'axios'
import { configureAmplify } from '../config/amplify/auth'
import { useLocation, useNavigate } from 'react-router-dom'
import { showNotification } from '@mantine/notifications'
import { useCognitoInfo } from 'api/query/cognito'
import { UserToken } from 'api/dto/user-token'
import { environment } from 'config/environment'

const passwordlessAxios = axios.create({
  baseURL: environment.restApi.baseUrl,
})

interface ICognitoUser {
  email: string
  given_name: string
  family_name: string
}

const authContext = createContext<ReturnType<typeof useProvideAuth>>({} as never)

export function ProvideAuth({ children }: { children: ReactNode }): JSX.Element {
  const currentAuth = useProvideAuth()
  return <authContext.Provider value={currentAuth}>{children}</authContext.Provider>
}

export const useAuth = () => useContext(authContext)

const useProvideAuth = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: infos, isLoading: isInfosLoading } = useCognitoInfo()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [tempEmail, setTempEmail] = useState('')
  const [federatedSignInLoading, setFederatedSignIn] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [tempCognitoUser, setTempCognitoUser] = useState<CognitoUser>()

  const [user, setUser] = useState<UserToken | null>(null)

  const appIsHydrated = useMemo(() => isHydrated, [isHydrated])

  const logout = useCallback(async (): Promise<void> => {
    await Auth.signOut()
    setIsAuthenticated(false)
    navigate('/session/login')
  }, [navigate])

  const extractAndRedirect = useCallback(async () => {
    const session = await Auth.currentSession()
    setRequestInterceptor(logout)

    if (!session) {
      return
    }

    const user = extractUserFromToken(session.getIdToken())

    setUser(user)
    setIsAuthenticated(true)

    if (location.pathname.includes('session')) {
      navigate('/tradeshows')
    }

    if (location.pathname.includes('auth/magic-link')) {
      navigate('/')
    }
  }, [location, logout, navigate])

  const hydrateUser = useCallback(async () => {
    if (isInfosLoading) {
      return
    }

    configureAmplify(infos)

    try {
      await extractAndRedirect()
    } catch (error) {
      // console.log("no user")
    }

    // if (location.pathname.includes('/change-password') && !tempCognitoUser) {
    //   navigate('/session/login')
    // }

    setIsHydrated(true)
  }, [isInfosLoading, infos, extractAndRedirect])

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const cognitoUser: CognitoUser & { challengeName: string } = await Auth.signIn({
        username: email.toLowerCase().trim(),
        password,
      })

      if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setTempCognitoUser(cognitoUser)
        navigate('/session/change-password')
        return
      }

      const session = await Auth.currentSession()

      if (session) {
        setRequestInterceptor(logout)
        setIsAuthenticated(true)
        setUser(extractUserFromToken(session.getIdToken()))

        navigate('/tradeshows')
      } else {
        showNotification({
          title: 'Login Failed',
          message: 'Seems like the email or password is not valid, please try again.',
          color: 'red',
        })
      }
    } catch (error: any) {
      showNotification({
        title: 'Login Failed',
        message: 'Seems like the email or password is not valid, please try again.',
        color: 'red',
      })
    }
  }

  const signUp = async (email: string, password: string, user: ICognitoUser) => {
    await Auth.signUp({
      username: email,
      password,
      attributes: {
        ...user,
      },
    })

    // if (import.meta.env.VITE_ENV !== 'PROD' && import.meta.env.VITE_ENV !== 'DEV') {
    await login(email, password)
    // }
  }

  const completeNewPassword = useCallback(
    async (newPassword: string): Promise<void> => {
      if (!tempCognitoUser) {
        return
      }

      try {
        await Auth.completeNewPassword(tempCognitoUser, newPassword, {})
        showNotification({
          message: 'Password has been changed successfully',
          color: 'green',
        })
        await extractAndRedirect()
      } catch (error: any) {
        if (error.code === 'Code mismatch') {
          showNotification({
            message: 'Code does not match',
            color: 'red',
          })
          return
        }
        showNotification({
          message: 'Change password failed, try again',
          color: 'red',
        })
      }
    },
    [tempCognitoUser, extractAndRedirect],
  )

  const forgotPassword = useCallback(
    async (email: string): Promise<void> => {
      // we set tempAuth for further use in resetPassword
      const currentEmail = email.toLowerCase().trim()
      setTempEmail(currentEmail)

      try {
        await Auth.forgotPassword(currentEmail)
        showNotification({
          title: 'Forgot Password',
          message: 'A code has been sent to your email, please check your email.',
          color: 'blue',
        })

        navigate('/session/reset-password')
      } catch (error) {
        showNotification({
          title: 'Forgot Password',
          message: 'Seems like the email is not valid, please enter a valid email.',
          color: 'red',
        })
      }
    },
    [navigate],
  )

  const resetPassword = useCallback(
    async (verificationCode: string, newPassword: string): Promise<void> => {
      if (!tempEmail) {
        return
      }

      try {
        await Auth.forgotPasswordSubmit(tempEmail, verificationCode, newPassword)
        showNotification({
          title: 'Change Password',
          message: 'Password has been changed successfully',
          color: 'green',
        })
        navigate('/session/login')
      } catch (error: any) {
        if (error.code === 'CodeMismatchException') {
          showNotification({
            title: 'Change Password',
            message: 'An error occurred, the code does not match, please try again.',
            color: 'red',
          })
          return
        }
        showNotification({
          title: 'Change Password',
          message: 'An error occurred, please try again',
          color: 'red',
        })
      }
    },
    [tempEmail, navigate],
  )

  const signInWithGoogle = async () => {
    setFederatedSignIn(true)
    await Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
    })
  }

  const completeSignInWithGoogle = useCallback(() => {
    setIsAuthenticated(true)
    setFederatedSignIn(false)
  }, [])

  const signInWithEmail = async (email: string) => {
    try {
      await passwordlessAxios.post('/auth/magic-link/initiate', { email })
    } catch (error) {
      // ignore errors
    }
  }

  const verifyMagicLink = useCallback(
    async (email: string, token: string, session: string): Promise<void> => {
      try {
        const { data } = await passwordlessAxios.post('/auth/magic-link/verify', { token, email, session })

        const { idToken, refreshToken, accessToken } = data

        // Store tokens in localStorage for Amplify to pick up during hydration
        const userPoolId = infos?.CognitoUserPoolId
        const clientId = infos?.CognitoAppClientID

        if (userPoolId && clientId) {
          const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
          const userKey = `CognitoIdentityServiceProvider.${clientId}.${data.email || 'user'}`

          // Store the tokens in the format Amplify expects
          localStorage.setItem(lastAuthUserKey, data.email || 'user')
          localStorage.setItem(`${userKey}.idToken`, idToken)
          localStorage.setItem(`${userKey}.refreshToken`, refreshToken)
          localStorage.setItem(`${userKey}.accessToken`, accessToken)
          localStorage.setItem(`${userKey}.tokenScopesAccepted`, '1')
        }

        showNotification({
          title: 'Login Successful',
          message: 'You have been logged in successfully.',
          color: 'green',
        })
        await extractAndRedirect()
      } catch (error) {
        showNotification({
          title: 'Login Failed',
          message: 'The magic link is invalid or expired, please try again.',
          color: 'red',
        })
        navigate('/session/login')
      }
    },
    [extractAndRedirect, infos?.CognitoUserPoolId, infos?.CognitoAppClientID, navigate],
  )

  useEffect(() => {
    void hydrateUser()
  }, [hydrateUser])

  useEffect(() => {
    const callback = hubCallback(completeSignInWithGoogle, logout)
    const removeAuthListener = Hub.listen('auth', callback)

    return () => removeAuthListener()
  }, [logout, completeSignInWithGoogle])

  return {
    user,
    login,
    completeNewPassword,
    logout,
    appIsHydrated,
    signUp,
    forgotPassword,
    resetPassword,
    isAuthenticated,
    signInWithGoogle,
    completeSignInWithGoogle,
    federatedSignInLoading,
    signInWithEmail,
    verifyMagicLink,
  }
}

export function extractUserFromToken(token: CognitoIdToken) {
  const payload = token.payload as Record<string, string>

  return new UserToken({
    id: payload.userId,
    email: payload.email,
    isAdmin: payload.isAdmin == 'true',
    tradeShowId: payload.tradeShowId,
    exhibitorId: payload.exhibitorId,
  })
}
